import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  deleteDoc,
  query
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../firebase/config';
import { AlarmSet } from '../types';

// 사용자 인증 서비스
export const authService = {
  // 회원가입
  async signUp(email: string, password: string) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      throw error;
    }
  },

  // 로그인
  async signIn(email: string, password: string) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      throw error;
    }
  },

  // 로그아웃
  async signOut() {
    try {
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  },

  // 인증 상태 변경 감지
  onAuthStateChanged(callback: (user: User | null) => void) {
    return onAuthStateChanged(auth, callback);
  }
};

// 알람 데이터 서비스
export const alarmService = {
  // 사용자의 알람 세트 목록 가져오기
  async getAlarmSets(userId: string): Promise<AlarmSet[]> {
    try {
      const q = query(
        collection(db, 'users', userId, 'alarmSets')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AlarmSet[];
    } catch (error) {
      console.error('알람 세트 가져오기 실패:', error);
      throw error;
    }
  },

  // 알람 세트 저장
  async saveAlarmSet(userId: string, alarmSet: AlarmSet) {
    try {
      const docRef = doc(db, 'users', userId, 'alarmSets', alarmSet.id);
      await setDoc(docRef, {
        ...alarmSet,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('알람 세트 저장 실패:', error);
      throw error;
    }
  },

  // 알람 세트 삭제
  async deleteAlarmSet(userId: string, alarmSetId: string) {
    try {
      await deleteDoc(doc(db, 'users', userId, 'alarmSets', alarmSetId));
    } catch (error) {
      console.error('알람 세트 삭제 실패:', error);
      throw error;
    }
  },

  // 사용자 프로필 저장
  async saveUserProfile(userId: string, profile: { name: string; class: string; userType?: string; termsAgreed?: boolean; termsAgreedAt?: string }) {
    try {
      await setDoc(doc(db, 'users', userId), {
        ...profile,
        updatedAt: new Date()
      }, { merge: true });
    } catch (error) {
      console.error('프로필 저장 실패:', error);
      throw error;
    }
  },

  // 사용자 프로필 가져오기
  async getUserProfile(userId: string) {
    try {
      const docRef = doc(db, 'users', userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data();
      }
      return null;
    } catch (error) {
      console.error('프로필 가져오기 실패:', error);
      throw error;
    }
  },

  // 사용자별 알람 데이터 저장 (로컬 스토리지와 Firestore 모두)
  async saveUserAlarmData(userId: string, alarmSets: AlarmSet[]) {
    try {
      // Firestore에 저장
      for (const alarmSet of alarmSets) {
        await this.saveAlarmSet(userId, alarmSet);
      }
      
      // 로컬 스토리지에도 저장 (백업용)
      const { saveAlarmSets } = await import('../utils/storage');
      saveAlarmSets(alarmSets, userId);
    } catch (error) {
      console.error('알람 데이터 저장 실패:', error);
      throw error;
    }
  },

  // 사용자별 알람 데이터 로드 (Firestore 우선, 실패시 로컬 스토리지)
  async loadUserAlarmData(userId: string): Promise<AlarmSet[]> {
    try {
      // Firestore에서 로드 시도
      const firestoreData = await this.getAlarmSets(userId);
      if (firestoreData.length > 0) {
        return firestoreData;
      }
      
      // Firestore에 데이터가 없으면 로컬 스토리지에서 로드
      const { loadAlarmSets } = await import('../utils/storage');
      return loadAlarmSets(userId);
    } catch (error) {
      console.error('Firestore에서 알람 데이터 로드 실패, 로컬 스토리지에서 로드:', error);
      // 로컬 스토리지에서 로드
      const { loadAlarmSets } = await import('../utils/storage');
      return loadAlarmSets(userId);
    }
  },

  // 클래스별 알람 데이터 저장 (기존 데이터 완전 교체)
  async saveClassAlarmData(userId: string, classId: string, alarmSets: AlarmSet[]) {
    try {
      // 기존 클래스별 알람세트 데이터를 모두 삭제
      const existingQuery = query(
        collection(db, 'users', userId, 'classes', classId, 'alarmSets')
      );
      const existingSnapshot = await getDocs(existingQuery);
      
      // 기존 데이터 삭제
      const deletePromises = existingSnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);
      
      // 새로운 알람세트 데이터 저장
      const savePromises = alarmSets.map(alarmSet => 
        setDoc(doc(db, 'users', userId, 'classes', classId, 'alarmSets', alarmSet.id), {
          ...alarmSet,
          updatedAt: new Date()
        })
      );
      await Promise.all(savePromises);
      
      
    } catch (error) {
      console.error('클래스별 알람 데이터 저장 실패:', error);
      throw error;
    }
  },

  // 클래스별 알람 데이터 로드
  async loadClassAlarmData(userId: string, classId: string): Promise<AlarmSet[]> {
    try {
      const q = query(
        collection(db, 'users', userId, 'classes', classId, 'alarmSets')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AlarmSet[];
    } catch (error) {
      console.error('클래스별 알람 데이터 로드 실패:', error);
      throw error;
    }
  },

  // 클래스별 알람 데이터 삭제 (클래스 삭제 시 사용)
  async deleteClassAlarmData(userId: string, classId: string) {
    try {
      // 클래스별 알람세트 데이터를 모두 삭제
      const existingQuery = query(
        collection(db, 'users', userId, 'classes', classId, 'alarmSets')
      );
      const existingSnapshot = await getDocs(existingQuery);
      
      // 기존 데이터 삭제
      const deletePromises = existingSnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);
      
      console.log(`클래스 ${classId}의 알람 데이터가 성공적으로 삭제되었습니다.`);
    } catch (error) {
      console.error('클래스별 알람 데이터 삭제 실패:', error);
      throw error;
    }
  }
};

// 파일 저장 서비스
export const storageService = {
  // 알람 음원 업로드
  async uploadAlarmAudio(userId: string, alarmId: string, file: File): Promise<string> {
    try {
      const storageRef = ref(storage, `users/${userId}/alarms/${alarmId}.mp3`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error('음원 업로드 실패:', error);
      throw error;
    }
  },

  // 알람 음원 삭제
  async deleteAlarmAudio(userId: string, alarmId: string) {
    try {
      const storageRef = ref(storage, `users/${userId}/alarms/${alarmId}.mp3`);
      await deleteObject(storageRef);
    } catch (error) {
      console.error('음원 삭제 실패:', error);
      throw error;
    }
  }
}; 